// /evidence — the credibility framework page.
//
// Uses the `.section` / `.section__inner` / `.cap-card` marketing
// system so it sits alongside `/features` and `/docs/*` visually.
// Emits Article + BreadcrumbList schema.
//
// Audience-appropriate capability language only — no internal
// identifiers, parameter values, or implementation paths.

import { Link } from "react-router-dom";
import {
    Archive,
    BookOpenCheck,
    FileCheck2,
    GitBranch,
    Repeat,
    ScrollText,
    ShieldCheck,
    Target,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import {
    buildArticleSchema,
    buildBreadcrumbListSchema,
} from "@/lib/schema";

const DATE_PUBLISHED = "2026-05-02";

const PILLARS = [
    {
        eyebrow: "// 01 · MODEL REGISTRY",
        title: "Every model, labelled. Every label, defensible.",
        icon: <Archive size={22} />,
        body:
            "Every interceptor, threat, sensor, and payload model lives in a registry with an explicit label — validated, provisional, or experimental — a version number, a validity envelope, and a model card explaining its provenance, test rig, and known limitations. When a run references a model, that model's id and version pin are baked into the run's compiled scenario and output manifest. A reviewer can always ask 'which model fed this number?' and get an auditable answer.",
        bullets: [
            "Labels: validated · provisional · experimental",
            "Validity envelopes — mass, speed, altitude, threat-class bounds",
            "Model cards carry provenance, test rig, known limitations",
            "Version pins baked into every run's compiled scenario",
        ],
    },
    {
        eyebrow: "// 02 · VALIDITY ENVELOPES",
        title: "Published boundaries, enforced at run submit.",
        icon: <Target size={22} />,
        body:
            "The platform refuses to run a scenario whose conditions fall outside the validity envelope of the models it would need. Ask for a 1.6 kg, 240 m/s threat using a model rated to 110 m/s and the run is rejected with a clear envelope-violation message — before any Monte Carlo work is done. A procurement record of a rejected run is itself powerful evidence that the platform's boundaries are enforced, not suggested.",
        bullets: [
            "Envelope violations blocked at the validation gate, not at output",
            "Rejected runs are themselves an auditable evidence artefact",
            "Envelope boundaries surface on the library page for every model",
        ],
    },
    {
        eyebrow: "// 03 · REGRESSION HARNESS",
        title: "Catch the 2 % shift before live-fire does.",
        icon: <GitBranch size={22} />,
        body:
            "Approved runs can be promoted to a golden run — a blessed deterministic baseline. Subsequent runs covering the same scenario are compared automatically and report either zero-drift or a structured drift record naming the specific metric that moved and by how much. This catches the model change that silently shifted your predicted hit probability by 2 % — the kind of shift nobody notices until the live-fire window closes.",
        bullets: [
            "Promote-to-golden gated behind reviewer approval",
            "Automated drift detection with structured per-metric records",
            "Drift-observed runs flagged; changes cannot land silently",
            "Same discipline engineers use to prevent flaky tests",
        ],
    },
    {
        eyebrow: "// 04 · CALIBRATION AUDIT",
        title: "No parameter change enters production silently.",
        icon: <FileCheck2 size={22} />,
        body:
            "When real-world telemetry arrives — PX4 ULog, ArduPilot binary, MAVLink tlog, CSV — engineers propose parameter deltas against a model. The platform records the proposal, replays the engagement with the proposed parameters, and captures before/after metrics. An admin accepts or rejects; acceptance bumps the model version and leaves an audit entry a reviewer can open months later to understand why the numbers shifted.",
        bullets: [
            "Log formats: PX4 ULog, ArduPilot binary, MAVLink tlog, CSV",
            "Before/after replay captured against the same seed set",
            "Admin accept/reject gate with reviewer notes",
            "Acceptance bumps the model version; nothing changes quietly",
        ],
    },
    {
        eyebrow: "// 05 · REPLAY DETERMINISM",
        title: "The same inputs produce the same outputs. Always.",
        icon: <Repeat size={22} />,
        body:
            "Given the same compiled scenario and the same Monte Carlo seed, UAVSimNet reproduces the same output. Every time. Bit-exact on the kinematic backend; metric-exact on the JSBSim-hybrid backend — identical miss distances, identical failure modes. This is what lets regression comparisons work at all, and what lets a scrutiny panel re-run your numbers independently six months after the fact.",
        bullets: [
            "Bit-exact replay on the kinematic backend",
            "Metric-exact replay on the JSBSim-hybrid backend",
            "Seed · scenario · version captured on every run",
            "Independent third-party replay with nothing but the run record",
        ],
    },
    {
        eyebrow: "// 06 · STRUCTURED REPORTS",
        title: "One source of truth. Three formats. Zero contradiction.",
        icon: <ScrollText size={22} />,
        body:
            "Every run produces the compiled scenario (YAML), the numerical record (JSON + CSV), and the decision document (PDF and DOCX with embedded plots). The structured formats are what engineers and analysts consume; the decision document is what programme offices hand to reviewers. Both come from the same run, with the same provenance snapshot baked in, so there is no way for the deck to say one thing and the engineering record to say another.",
        bullets: [
            "Compiled scenario (YAML) — the inputs, canonicalised",
            "Numerical record (JSON + CSV) — every metric, every seed",
            "Decision document (PDF + DOCX) — navy-themed, plot-embedded",
            "Same source of truth feeds every format",
        ],
    },
    {
        eyebrow: "// 07 · IMMUTABLE RUN SNAPSHOT",
        title: "What ran is what ran — permanently.",
        icon: <ShieldCheck size={22} />,
        body:
            "Submitting a run freezes an immutable snapshot of every parameter that fed it — source, confidence, citation, and who last touched each value. Mutating the underlying library later does not mutate the snapshot. The run's evidence is locked the moment it starts, so a reviewer opening it months later sees exactly the state that produced the numbers.",
        bullets: [
            "Snapshot captured at run submit, before execution begins",
            "Grouped by capability, with source + confidence + citation",
            "Preserves value at time of capture regardless of library edits",
            "Queryable via the run detail page and the exported report",
        ],
    },
];

export default function EvidencePage() {
    return (
        <>
            <SeoHead
                path="/evidence"
                schemaJsonLd={[
                    buildArticleSchema({
                        headline:
                            "The UAVSimNet Credibility Framework — Evidence, Not Slides",
                        description:
                            "How UAVSimNet's model registry, regression harness, validity envelopes, replay determinism, and immutable run snapshots make every counter-UAS claim defensible.",
                        datePublished: DATE_PUBLISHED,
                        path: "/evidence",
                    }),
                    buildBreadcrumbListSchema({
                        path: "/evidence",
                        label: "Evidence",
                    }),
                ]}
            />
            <div data-testid="evidence-page">
                <section className="section" style={{ paddingTop: 64 }}>
                    <div className="section__inner">
                        <span className="section__eyebrow">// CREDIBILITY FRAMEWORK</span>
                        <h1
                            className="section__heading"
                            data-testid="evidence-h1"
                        >
                            Evidence, not slides.
                        </h1>
                        <p className="section__lede">
                            Most counter-UAS simulation vendors sell you graphs. UAVSimNet
                            sells you <em>evidence</em> — a defensible chain of records
                            that links every claim in a slide deck back to the physical
                            models, the calibration data, and the deterministic runs that
                            produced them. Seven pillars hold the chain up. Pull on any
                            one and the rest tightens.
                        </p>
                    </div>
                </section>

                {PILLARS.map((p, idx) => (
                    <section
                        key={p.title}
                        className={"section" + (idx % 2 === 1 ? " section--alt" : "")}
                        data-testid={`evidence-pillar-${idx}`}
                    >
                        <div
                            className="section__inner"
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 2fr",
                                gap: 48,
                                alignItems: "start",
                            }}
                        >
                            <div className="scroll-reveal">
                                <div className="cap-card__icon">{p.icon}</div>
                                <span className="section__eyebrow">{p.eyebrow}</span>
                                <h2
                                    className="section__heading"
                                    style={{ fontSize: 26, lineHeight: 1.25 }}
                                >
                                    {p.title}
                                </h2>
                            </div>
                            <div className="scroll-reveal" data-delay="120">
                                <p className="section__lede" style={{ marginBottom: 18 }}>
                                    {p.body}
                                </p>
                                <ul className="cap-card__list">
                                    {p.bullets.map((b) => (
                                        <li key={b}>{b}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>
                ))}

                <section className="section">
                    <div className="section__inner" style={{ textAlign: "center" }}>
                        <span className="section__eyebrow">// NEXT</span>
                        <h2 className="section__heading">
                            Take the evidence further.
                        </h2>
                        <p
                            className="section__lede"
                            style={{ margin: "0 auto 24px", maxWidth: 720 }}
                        >
                            The credibility framework underwrites every capability surface
                            and every use case. Read on or request a briefing.
                        </p>
                        <div
                            style={{
                                display: "inline-flex",
                                gap: 12,
                                flexWrap: "wrap",
                                justifyContent: "center",
                            }}
                        >
                            <Link
                                to="/features"
                                className="btn-primary"
                                data-testid="evidence-cta-features"
                            >
                                Capability tour
                            </Link>
                            <Link
                                to="/use-cases"
                                className="btn-ghost"
                                data-testid="evidence-cta-use-cases"
                            >
                                Use cases
                            </Link>
                            <Link
                                to="/docs/evidence"
                                className="btn-ghost"
                                data-testid="evidence-cta-docs"
                            >
                                <BookOpenCheck size={14} style={{ marginRight: 6 }} /> Docs · evidence
                            </Link>
                            <Link
                                to="/contact"
                                className="btn-ghost"
                                data-testid="evidence-cta-contact"
                            >
                                Request a briefing
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
